// src/pages/Orders/ExecutiveOrders.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import orderService from '../../services/order.service';
import Toast from '../../components/Toast/Toast';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { ROLES } from '../../constants/roles';
import './OrderPages.css';

const STATUS_TABS = [
  { key: 'ALL',                  label: 'All' },
  { key: 'PENDING_CONFIRMATION', label: 'Pending Confirmation' },
  { key: 'CONFIRMED',            label: 'Confirmed' },
  { key: 'REJECTED',             label: 'Rejected' },
  { key: 'PARTIALLY_DISPATCHED', label: 'Partial' },
  { key: 'DISPATCHED',           label: 'Dispatched' },
  { key: 'DELIVERED',            label: 'Delivered' },
  { key: 'CANCELLED',            label: 'Cancelled' },
];

const STATUS_COLORS = {
  DRAFT:                { bg: '#f8f9fa', color: '#6c757d' },
  PENDING_CONFIRMATION: { bg: '#fff8e1', color: '#f57f17' },
  CONFIRMED:            { bg: '#e8f5e9', color: '#2e7d32' },
  REJECTED:             { bg: '#fce4ec', color: '#c62828' },
  PARTIALLY_DISPATCHED: { bg: '#e3f2fd', color: '#1565c0' },
  DISPATCHED:           { bg: '#e0f7fa', color: '#00695c' },
  DELIVERED:            { bg: '#e8f5e9', color: '#1b5e20' },
  FAILED_TO_DELIVER:    { bg: '#fce4ec', color: '#c62828' },
  CANCELLED:            { bg: '#fce4ec', color: '#b71c1c' },
};

// ✅ Roles that can confirm/deny orders
const CAN_CONFIRM_ROLES = [
  ROLES.EXECUTIVE,
  ROLES.AREA_MANAGER,
  ROLES.ZONAL_MANAGER,
  ROLES.ADMIN,
  ROLES.OFFICE_ADMIN,
  ROLES.SUPER_ADMIN,
];

// ✅ Global roles have no fixed companyId on their JWT — they rely on the
//    company chosen in the top navbar switcher (shared, not a per-page dropdown).
const GLOBAL_ROLES = [ROLES.SUPER_ADMIN, ROLES.OFFICE_ADMIN];

const PAGE_SIZE = 20;

const OrderStatusBadge = ({ status }) => {
  const style = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: style.bg, color: style.color, whiteSpace: 'nowrap',
    }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const ExecutiveOrders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ SUPER_ADMIN and OFFICE_ADMIN behave identically as global roles.
  const isGlobalRole = GLOBAL_ROLES.includes(user?.role);

  // ✅ Company chosen in the top navbar switcher. Read it here instead of
  //    rendering a second dropdown on the page.
  //    NOTE: adjust this line to match wherever your navbar switcher stores it
  //    (localStorage key / CompanyContext / AuthContext field).
  const selectedCompanyId = isGlobalRole
    ? localStorage.getItem('selectedCompanyId') || ''
    : undefined;

  const canConfirm = CAN_CONFIRM_ROLES.includes(user?.role);

  const [orders,     setOrders]     = useState([]);
  const [activeTab,  setActiveTab]  = useState('ALL');
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [rejecting,  setRejecting]  = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [confirmModal, setConfirmModal] = useState({ open: false, orderId: null, ordName: '' });
  const [rejectModal,  setRejectModal]  = useState({ open: false, orderId: null, ordName: '', reason: '' });

  // ── Fetch orders ──────────────────────────────────────────────────────────
  //  - Company-scoped roles: fetch immediately; backend reads companyId from JWT.
  //  - Global roles: use the navbar-selected company; refetch when it changes.
  useEffect(() => {
    if (!isGlobalRole) {
      fetchOrders();
    } else if (selectedCompanyId) {
      fetchOrders();
    }
  }, [isGlobalRole, selectedCompanyId]);

  // ✅ Reset to page 1 whenever the tab or the underlying order set changes
  useEffect(() => { setCurrentPage(1); }, [activeTab, selectedCompanyId]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await orderService.getOrders(
        null,
        isGlobalRole ? selectedCompanyId : undefined
      );
      if (res.success) setOrders(res.data || []);
    } catch (_) {
      setToast({ message: 'Failed to fetch orders', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filtered = activeTab === 'ALL'
    ? orders
    : orders.filter(o => o.orderStatus === activeTab);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedOrders = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const openConfirmModal = (order) =>
    setConfirmModal({ open: true, orderId: order.id, ordName: order.orderId });

  const handleConfirmSubmit = async () => {
    setConfirming(confirmModal.orderId);
    setConfirmModal(prev => ({ ...prev, open: false }));
    try {
      await orderService.confirmOrder(confirmModal.orderId);
      setToast({ message: `Order ${confirmModal.ordName} confirmed!`, type: 'success' });
      fetchOrders();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to confirm', type: 'error' });
    } finally { setConfirming(null); }
  };

  const openRejectModal = (order) =>
    setRejectModal({ open: true, orderId: order.id, ordName: order.orderId, reason: '' });

  const handleRejectSubmit = async () => {
    setRejecting(rejectModal.orderId);
    setRejectModal(prev => ({ ...prev, open: false }));
    try {
      await orderService.rejectOrder(rejectModal.orderId, { reason: rejectModal.reason });
      setToast({ message: `Order ${rejectModal.ordName} rejected`, type: 'success' });
      fetchOrders();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to reject', type: 'error' });
    } finally { setRejecting(null); }
  };

  return (
    <div className="order-page-container" style={{ maxWidth: 1100 }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Confirm Order"
        message={`Are you sure you want to confirm order ${confirmModal.ordName}? This will send it to the godown for dispatch.`}
        confirmLabel="✓ Confirm Order"
        confirmType="success"
        onConfirm={handleConfirmSubmit}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      <ConfirmModal
        isOpen={rejectModal.open}
        title="Deny Order"
        message={`You are about to deny order ${rejectModal.ordName}. The Sales Officer will be notified.`}
        confirmLabel="✕ Deny Order"
        confirmType="danger"
        onConfirm={handleRejectSubmit}
        onClose={() => setRejectModal(prev => ({ ...prev, open: false }))}
      >
        <label className="cm-reason-label">
          Reason for denial <span style={{ color: '#adb5bd', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          className="cm-reason-input"
          rows={3}
          placeholder="e.g. Product out of stock, incorrect pricing..."
          value={rejectModal.reason}
          onChange={e => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
        />
      </ConfirmModal>

      <div className="order-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h4 className="order-title">Orders</h4>
          <p className="order-subtitle">
            {user?.role === ROLES.SALES_OFFICER
              ? 'Your placed orders'
              : 'Review and manage orders from your team'}
          </p>
        </div>
      </div>

      {/* ✅ Global roles rely on the navbar company switcher — no per-page dropdown.
          Only prompt if nothing is selected there yet. */}
      {isGlobalRole && !selectedCompanyId ? (
        <div className="order-no-data" style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>
          Select a company from the top bar to view its orders.
        </div>
      ) : (
        <>
          <div className="order-tabs">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.key}
                className={`order-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.key !== 'ALL' && (
                  <span className="order-tab-count">
                    {orders.filter(o => o.orderStatus === tab.key).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="order-table-container">
            {loading ? (
              <div className="order-loading">Loading orders...</div>
            ) : (
              <table className="order-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Dealer</th>
                    <th>Godown</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>SO</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.length > 0 ? (
                    paginatedOrders.map(order => (
                      <tr key={order.id}>
                        <td className="order-id-cell">{order.orderId}</td>
                        <td>{new Date(order.orderDate).toLocaleDateString('en-IN')}</td>
                        <td>{order.dealer?.dealerShopName || '—'}</td>
                        <td>{order.godown?.name || '—'}</td>
                        <td>
                          <span className="order-items-badge">
                            {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="order-amount">
                          ₹{Number(order.totalAmount).toLocaleString('en-IN')}
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {order.createdByUser?.firstName} {order.createdByUser?.lastName}
                        </td>
                        <td>
                          <div>
                            <OrderStatusBadge status={order.orderStatus} />
                            {order.orderStatus === 'REJECTED' && order.rejectionReason && (
                              <div style={{ fontSize: 11, color: '#c62828', marginTop: 4, maxWidth: 140, wordBreak: 'break-word' }}>
                                Reason: {order.rejectionReason}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="order-btn-view"
                              onClick={() => navigate(`/orders/detail/${order.id}`)}
                            >
                              View
                            </button>

                            {canConfirm && order.orderStatus === 'PENDING_CONFIRMATION' && (
                              <>
                                <button
                                  className="order-btn-confirm"
                                  onClick={() => openConfirmModal(order)}
                                  disabled={confirming === order.id}
                                >
                                  {confirming === order.id ? '...' : '✓ Confirm'}
                                </button>
                                <button
                                  className="order-btn-reject"
                                  onClick={() => openRejectModal(order)}
                                  disabled={rejecting === order.id}
                                >
                                  {rejecting === order.id ? '...' : '✕ Deny'}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="order-no-data">No orders found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {!loading && filtered.length > PAGE_SIZE && (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: 12, padding: '16px 0',
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid #dee2e6',
                    background: currentPage === 1 ? '#f5f5f5' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13,
                  }}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: 13, color: '#495057' }}>
                  Page {currentPage} of {totalPages} · {filtered.length} orders
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid #dee2e6',
                    background: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: 13,
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ExecutiveOrders;